import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import "./TeamNamePicker.css";

function normalizeTeams(teams) {
  const seenIds = new Set();

  return (Array.isArray(teams) ? teams : []).reduce((result, team) => {
    const id = typeof team?.id === "string" ? team.id.trim() : "";
    if (!id || seenIds.has(id)) return result;

    seenIds.add(id);
    result.push({
      id,
      name: typeof team?.name === "string" && team.name.trim() ? team.name.trim() : "Unnamed Team",
    });
    return result;
  }, []);
}

export default function TeamNamePicker({
  teams,
  value,
  onChange,
  fallbackLabel = "Unnamed Team",
  disabled = false,
  className = "",
  ariaLabel = "Switch active team",
}) {
  const rootRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const options = normalizeTeams(teams);
  const selectedTeam = options.find((team) => team.id === value) || {
    id: value || "",
    name: fallbackLabel,
  };
  const canSwitch = !disabled && (options.length > 1 || (options.length === 1 && options[0].id !== value));

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!canSwitch) {
    return (
      <span className={`TeamNamePicker TeamNamePicker--static ${className}`.trim()}>
        {selectedTeam.name}
      </span>
    );
  }

  return (
    <span
      ref={rootRef}
      className={`TeamNamePicker ${isOpen ? "is-open" : ""} ${className}`.trim()}
    >
      <button
        type="button"
        className="TeamNamePicker__trigger"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="TeamNamePicker__label">{selectedTeam.name}</span>
        <ChevronDown aria-hidden="true" size={18} strokeWidth={2.4} />
      </button>

      {isOpen ? (
        <span className="TeamNamePicker__menu" role="listbox" aria-label={ariaLabel}>
          {options.map((team) => (
            <button
              type="button"
              role="option"
              aria-selected={team.id === selectedTeam.id}
              className={team.id === selectedTeam.id ? "is-selected" : ""}
              key={team.id}
              onClick={() => {
                setIsOpen(false);
                if (team.id !== selectedTeam.id) onChange?.(team.id);
              }}
            >
              {team.name}
            </button>
          ))}
        </span>
      ) : null}
    </span>
  );
}
